import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    CircularProgress,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Pagination,
    Radio,
    RadioGroup,
    Step,
    StepLabel,
    Stepper,
    Tab,
} from "@mui/material";
import Webcam from "react-webcam";
import * as faceapi from "face-api.js";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import CloseIcon from "@mui/icons-material/Close";
import DoneIcon from "@mui/icons-material/Done";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ImageIcon from "@mui/icons-material/Image";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { useTranslation } from "react-i18next";
import { useLoading } from "../../../context/LoadingContext";
import { useAuth } from "../../../context/AuthContext";

import {
    CustomDialog,
    CustomTabPanel,
    CustomTabsV2,
} from "../../../components/StyledComponents";

import {
    ControlpanelCancelButton,
    ControlpanelPrimaryButton,
    ControlpanelSecondaryButton,
    ControlpanelSuccess3thButton,
    GrayIconButton,
} from "../../../components/StyledButton";

import CustomSelect from "../../../components/CustomSelect";
import CustomDateTimePicker from "../../../components/CustomDateTimeRangePicker";
import DropZoneInput from "../../../components/DropZoneInput";
import VideoIcon48pxSvg from "../../../assets/images/icons/VideoIcon48pxSvg";
import CaptureIcon16pxSvg from "../../../assets/images/icons/CaptureIcon16pxSvg";
import RefreshCircleIcon20pxSvgV2 from "../../../assets/images/icons/RefreshCircleIcon20pxSvgV2";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import { getImgEvent } from "../../../axios/Eventlog/EventLogApi";
import { toast } from "react-toastify";
import { addEventsLogsVisitor } from "../../../axios/Visitor/VisitorApi";

dayjs.extend(utc);
dayjs.extend(timezone);

const videoConstraints = {
    facingMode: "user",
};

function AvatarCard({
    fullName,
    time,
    imageBase64,
    isSelected,
    onSelect,
}) {
    const { t } = useTranslation();

    const localTime = dayjs
        .utc(time)
        .local()
        .format("HH:mm:ss");

    return (
        <div className="text-center w-full flex flex-col">
            <div
                className={`
          relative h-32 w-32 overflow-hidden rounded-lg
          ${isSelected ? "ring-2 ring-blue-500" : ""}
          group cursor-pointer flex items-center justify-center
        `}
                onClick={onSelect}
            >
                <img
                    src={imageBase64}
                    alt={
                        fullName ||
                        t("common.unidentified-user")
                    }
                    className="w-full h-full object-cover transition-all duration-200"
                />

                <div
                    className="
            absolute inset-0 bg-black/40 opacity-0
            group-hover:opacity-100
            transition-opacity duration-200
          "
                />

                <div
                    className="
            absolute inset-0 rounded-lg
            ring-0 group-hover:ring-2 group-hover:ring-blue-500
            transition-all duration-200
            pointer-events-none
          "
                />

                {isSelected && (
                    <div className="absolute top-2 right-2 bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center shadow-md">
                        <DoneIcon
                            style={{
                                width: "16px",
                                height: "16px",
                            }}
                        />
                    </div>
                )}
            </div>

            <div className="mt-2 font-semibold text-sm text-left">
                {fullName ||
                    t("common.unidentified-user")}
            </div>

            <div className="flex items-center gap-1 mt-1 text-sm text-gray-600">
                <AccessTimeIcon
                    style={{
                        width: "16px",
                        height: "17px",
                    }}
                />
                <span>{localTime}</span>
            </div>
        </div>
    );
}

const AddEventLogDialog = ({
    dataInfo,
    data,
    selectedDate,
    onClose,
    onRefresh,
}) => {
    const [show, hide] = useLoading();
    const { t } = useTranslation();
    const { dateTimeFormat } = useAuth();

    const steps = [
        t("common.select-image"),
        t("button.add-event"),
    ];

    const [activeStep, setActiveStep] =
        useState(0);

    const [tabValue, setTabValue] = useState(0);
    const webcamRef = useRef(null);
    const previousFaceRef = useRef(null);

    const [capturedImage, setCapturedImage] = useState(null);
    const [cameraError, setCameraError] = useState(false);
    const [modelsLoaded, setModelsLoaded] = useState(false);
    const [faceValid, setFaceValid] = useState(false);
    const [faceMessage, setFaceMessage] = useState("");
    const [blinkDetected, setBlinkDetected] = useState(false);
    const [isDetecting, setIsDetecting] = useState(false);
    const [fileImport, setFileImport] = useState(null);

    const [selectedImageId, setSelectedImageId] =
        useState(null);

    const [
        isImageSectionOpen,
        setIsImageSectionOpen,
    ] = useState(true);

    const a11yProps = (index) => ({
        id: `event-log-tab-${index}`,
        "aria-controls": `event-log-tabpanel-${index}`,
    });

    const convertFileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
        });
    };

    const getEyeAspectRatio = (eye) => {
        const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
        const A = distance(eye[1], eye[5]);
        const B = distance(eye[2], eye[4]);
        const C = distance(eye[0], eye[3]);
        return (A + B) / (2.0 * C);
    };

    const validatePortraitImage = async (file) => {
        try {
            const image = await faceapi.bufferToImage(file);
            const detections = await faceapi
                .detectAllFaces(
                    image,
                    new faceapi.TinyFaceDetectorOptions()
                )
                .withFaceLandmarks();

            if (detections.length === 0) {
                return {
                    valid: false,
                    message: t("common.noface") || "Không tìm thấy khuôn mặt",
                };
            }

            if (detections.length > 1) {
                return {
                    valid: false,
                    message: t("common.multipleface") || "Chỉ được có 1 khuôn mặt",
                };
            }

            const face = detections[0];
            const box = face.detection.box;
            const imageWidth = image.width;
            const imageHeight = image.height;
            const faceArea = box.width * box.height;
            const totalArea = imageWidth * imageHeight;
            const ratio = faceArea / totalArea;

            if (ratio < 0.04) {
                return {
                    valid: false,
                    message: t("common.movecloser") || "Khuôn mặt quá nhỏ",
                };
            }

            const centerX = box.x + box.width / 2;
            const centerY = box.y + box.height / 2;
            const validX =
                centerX > imageWidth * 0.25 &&
                centerX < imageWidth * 0.75;
            const validY =
                centerY > imageHeight * 0.15 &&
                centerY < imageHeight * 0.85;

            if (!validX || !validY) {
                return {
                    valid: false,
                    message: t("common.centerface") || "Khuôn mặt phải nằm giữa ảnh",
                };
            }

            return {
                valid: true,
                message: t("common.validface") || "Khuôn mặt hợp lệ",
            };
        } catch (error) {
            console.error(error);
            return {
                valid: false,
                message: t("common.invalidimage") || "Ảnh không hợp lệ",
            };
        }
    };

    const resetFaceValidationState = () => {
        setBlinkDetected(false);
        setFaceValid(false);
        setFaceMessage("");
        previousFaceRef.current = null;
    };

    const handleChangeTab = (event, newValue) => {
        if (newValue === 2) return;

        setTabValue(newValue);
        setCapturedImage(null);
        setFileImport(null);
        resetFaceValidationState();
        setFormData((prev) => ({
            ...prev,
            faceImage: "",
        }));
    };

    const handleFileChange = async (name, file) => {
        if (!file) return;

        setIsDetecting(true);

        try {
            const result = await validatePortraitImage(file);
            setFaceValid(result.valid);
            setFaceMessage(result.message);

            if (!result.valid) {
                setFileImport(null);
                setFormData((prev) => ({
                    ...prev,
                    faceImage: "",
                }));
                return;
            }

            const base64 = await convertFileToBase64(file);
            setFileImport(file);
            setFormData((prev) => ({
                ...prev,
                faceImage: base64,
            }));
        } finally {
            setIsDetecting(false);
        }
    };

    const handleImageInputChange = async (e) => {
        const { name, files } = e.target;
        const file = files?.[0];
        if (!file) return;
        await handleFileChange(name, file);
    };

    const capture = useCallback(() => {
        if (!faceValid) return;

        const imageSrc = webcamRef.current?.getScreenshot();
        if (imageSrc) {
            setCapturedImage(imageSrc);
            setFormData((prev) => ({
                ...prev,
                faceImage: imageSrc,
            }));
        }
    }, [faceValid]);

    const handleRetake = () => {
        setCapturedImage(null);
        resetFaceValidationState();
        setFormData((prev) => ({
            ...prev,
            faceImage: "",
        }));
    };

    const [dataIMG, setDataIMG] = useState([]);

    const RECORD_PER_PAGE = 10;

    const [page, setPage] = useState(0);

    const [rowCount, setRowCount] =
        useState(0);

    const biometricTypeData = [
        { value: 'face', label: t("common.content-095") },
    ];

    const initialFormData = {
        userId: data[0]?.visitorId,
        eventType: 1,
        biometricType:
            biometricTypeData[0].value,
        timeGMT: "",
        selectDate: selectedDate || null,
        selectTime:
            dayjs().format("HH:mm:ss"),
        langId:
            localStorage.getItem(
                "i18nextLngId"
            ),
        faceImage: null,
    };

    const [formData, setFormData] =
        useState(initialFormData);

    const getFromToByDateWithTZ = (
        dateObj,
        timeZone
    ) => {
        if (!dateObj)
            return {
                from: null,
                to: null,
            };

        const base = dayjs(dateObj).tz(
            timeZone
        );

        return {
            from: base
                .startOf("day")
                .utc()
                .toISOString(),

            to: base
                .endOf("day")
                .utc()
                .toISOString(),
        };
    };

    useEffect(() => {
        if (!formData) return;

        const browserTZ =
            dayjs.tz.guess();

        const { from, to } =
            getFromToByDateWithTZ(
                formData?.selectDate,
                browserTZ
            );

        const fetchData = async () => {
            const payload = {
                page: page + 1,
                record: RECORD_PER_PAGE,
                from,
                to,
                isError: true,
                langId:
                    localStorage.getItem(
                        "i18nextLngId"
                    ),
            };

            try {
                show();

                const response =
                    await getImgEvent(payload);

                const imgList =
                    response.data.data || [];

                const sortedImgList = [
                    ...imgList,
                ]
                    .sort(
                        (a, b) =>
                            dayjs
                                .utc(b.time)
                                .valueOf() -
                            dayjs
                                .utc(a.time)
                                .valueOf()
                    )
                    .map((item, index) => ({
                        ...item,
                        uniqueId: `${item.time}-${index}`,
                    }));

                setDataIMG(sortedImgList);

                setRowCount(
                    response.data.pagination
                        .totalElements
                );
            } catch (error) {
                toast.error(
                    t("common.load-image-error")
                );
            } finally {
                hide();
            }
        };

        fetchData();
    }, [
        formData?.selectDate,
        page,
    ]);

    useEffect(() => {
        const loadModels = async () => {
            try {
                const MODEL_URL = "/portal/models";
                await faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL);
                await faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL);
                setModelsLoaded(true);
            } catch (error) {
                console.error(error);
            }
        };

        loadModels();
    }, []);

    useEffect(() => {
        let interval;

        if (tabValue === 1 && modelsLoaded && webcamRef.current) {
            interval = setInterval(async () => {
                try {
                    const video = webcamRef.current?.video;

                    if (
                        !video ||
                        video.readyState < 2 ||
                        video.videoWidth <= 0 ||
                        video.videoHeight <= 0
                    ) {
                        return;
                    }

                    const detections = await faceapi
                        .detectAllFaces(
                            video,
                            new faceapi.TinyFaceDetectorOptions()
                        )
                        .withFaceLandmarks();

                    if (detections.length === 0) {
                        setFaceValid(false);
                        setBlinkDetected(false);
                        previousFaceRef.current = null;
                        setFaceMessage(
                            t("common.noface") || "Không tìm thấy khuôn mặt"
                        );
                        return;
                    }

                    if (detections.length > 1) {
                        setFaceValid(false);
                        setBlinkDetected(false);
                        previousFaceRef.current = null;
                        setFaceMessage(
                            t("common.multipleface") || "Chỉ được có 1 khuôn mặt"
                        );
                        return;
                    }

                    const face = detections[0];
                    const box = face.detection.box;

                    const currentFace = {
                        x: Math.round(box.x),
                        y: Math.round(box.y),
                        width: Math.round(box.width),
                        height: Math.round(box.height),
                    };

                    if (previousFaceRef.current) {
                        const prev = previousFaceRef.current;
                        const diffX = Math.abs(prev.x - currentFace.x);
                        const diffY = Math.abs(prev.y - currentFace.y);
                        const diffW = Math.abs(prev.width - currentFace.width);
                        const diffH = Math.abs(prev.height - currentFace.height);
                        const isDifferentFace =
                            diffX > 60 ||
                            diffY > 60 ||
                            diffW > 60 ||
                            diffH > 60;

                        if (isDifferentFace) {
                            setBlinkDetected(false);
                            setFaceValid(false);
                            setFaceMessage(
                                t("common.blink") || "Vui lòng chớp mắt lại"
                            );
                        }
                    }

                    previousFaceRef.current = currentFace;

                    const videoWidth = video.videoWidth;
                    const videoHeight = video.videoHeight;
                    const faceArea = box.width * box.height;
                    const totalArea = videoWidth * videoHeight;
                    const ratio = faceArea / totalArea;

                    if (ratio < 0.05) {
                        setFaceValid(false);
                        setFaceMessage(
                            t("common.movecloser") || "Đưa mặt lại gần hơn"
                        );
                        return;
                    }

                    const centerX = box.x + box.width / 2;
                    const centerY = box.y + box.height / 2;
                    const validX =
                        centerX > videoWidth * 0.2 &&
                        centerX < videoWidth * 0.8;
                    const validY =
                        centerY > videoHeight * 0.15 &&
                        centerY < videoHeight * 0.85;

                    if (!validX || !validY) {
                        setFaceValid(false);
                        setFaceMessage(
                            t("common.centerface") || "Đưa mặt vào giữa khung"
                        );
                        return;
                    }

                    const landmarks = face.landmarks;
                    const leftEye = landmarks.getLeftEye();
                    const rightEye = landmarks.getRightEye();
                    const leftEAR = getEyeAspectRatio(leftEye);
                    const rightEAR = getEyeAspectRatio(rightEye);
                    const ear = (leftEAR + rightEAR) / 2;

                    if (ear < 0.32) {
                        setBlinkDetected(true);
                    }

                    if (!blinkDetected) {
                        setFaceValid(false);
                        setFaceMessage(
                            t("common.blink") ||
                            "Vui lòng chớp mắt để xác thực người thật"
                        );
                        return;
                    }

                    setFaceValid(true);
                    setFaceMessage(
                        t("common.validface") || "Khuôn mặt hợp lệ"
                    );
                } catch (error) {
                    console.error(error);
                }
            }, 500);
        }

        return () => clearInterval(interval);
    }, [tabValue, modelsLoaded, t, blinkDetected]);

    useEffect(() => {
        if (tabValue !== 1) return;

        navigator.mediaDevices
            ?.getUserMedia({ video: true })
            .then(() => {
                setCameraError(false);
            })
            .catch(() => {
                setCameraError(true);
            });
    }, [tabValue]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleCloseDialog = () => {
        setFormData(initialFormData);
        onClose();
    };

    const handleNext = () => {
        setActiveStep((prev) => prev + 1);
    };

    const handleBack = () => {
        setActiveStep((prev) => prev - 1);
    };



    const handleSubmit = async () => {
        try {
            if (
                !formData?.userId ||
                !formData?.eventType ||
                !formData?.selectDate ||
                !formData?.selectTime
            ) {
                toast.error(
                    t("common.fill-required")
                );
                return;
            }

            const date = dayjs(
                formData?.selectDate
            ).format("YYYY-MM-DD");

            const dateTimeLocal = `${date} ${formData?.selectTime}`;

            const timeGMT = dayjs(dateTimeLocal)
                .utc()
                .format("YYYY-MM-DDTHH:mm:ss[Z]");

            const cleanBase64 = formData.faceImage
                ? formData.faceImage.replace(/^data:image\/\w+;base64,/, "")
                : "";

            const payload = {
                ...formData,
                eventId: dataInfo?.id,
                visitorId: formData?.userId,
                attendanceDateTime: timeGMT,
                eventTypeId: formData?.eventType,
                faceImage: cleanBase64,
            };

            const response = await addEventsLogsVisitor(payload);
            if (
                response?.data?.responseCode === 0
            ) {

                toast.success(t("common.add-success"));
                setFormData(initialFormData);
                onClose();
                onRefresh?.();

            } else {

                toast.error(
                    response?.data?.data?.message ||
                    t("common.something-went-wrong")
                );
            }

        } catch (error) {

            console.error(error);

            toast.error(
                error?.response?.data?.message ||
                t("common.something-went-wrong") ||
                "Something went wrong"
            );
        }
    };

    return (
        <CustomDialog
            maxWidth={"md"}
            open
            onClose={handleCloseDialog}
            keepMounted
        >
            <DialogTitle>
                <div className="flex justify-between">
                    <div className="flex items-center gap-4">
                        <p className="text-xl font-semibold">
                            {t("button.add-event")}
                        </p>
                    </div>

                    <GrayIconButton
                        onClick={handleCloseDialog}
                    >
                        <CloseIcon />
                    </GrayIconButton>
                </div>
            </DialogTitle>

            <DialogContent>
                <div className="mt-6 mb-6">
                    <Stepper
                        activeStep={activeStep}
                        alternativeLabel
                        sx={{
                            "& .MuiStepLabel-label.Mui-active": {
                                color: "#0057FF",
                                fontWeight: 600,
                            },

                            "& .MuiStepLabel-label.Mui-completed": {
                                color: "#0057FF",
                                fontWeight: 600,
                            },

                            "& .MuiStepIcon-root.Mui-active": {
                                color: "#0057FF",
                            },

                            "& .MuiStepIcon-root.Mui-completed": {
                                color: "#0057FF",
                            },

                            "& .MuiStepConnector-root.Mui-active .MuiStepConnector-line, & .MuiStepConnector-root.Mui-completed .MuiStepConnector-line":
                            {
                                borderColor: "#0057FF",
                            },
                        }}
                    >
                        {steps.map((label) => (
                            <Step key={label}>
                                <StepLabel>
                                    {label}
                                </StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                </div>

                {/* STEP 1 */}
                {activeStep === 0 && (
                    <div className="space-y-3 mt-4">
                        {/* <div className="space-y-2">
                            <label>
                                {t(
                                    "common.date-time"
                                )}
                            </label>

                            <CustomDateTimePicker
                                mode="singleTime"
                                className="h-10"
                                value={
                                    formData.selectDate &&
                                        formData.selectTime
                                        ? dayjs(
                                            `${dayjs(
                                                formData.selectDate
                                            ).format(
                                                "YYYY-MM-DD"
                                            )} ${formData.selectTime
                                            }`
                                        ).toDate()
                                        : null
                                }
                                dateTimeFormat={
                                    dateTimeFormat
                                }
                                onChange={(
                                    newDateTime
                                ) => {
                                    const d =
                                        dayjs(
                                            newDateTime
                                        );

                                    setFormData(
                                        (prev) => ({
                                            ...prev,

                                            selectDate:
                                                d.toDate(),

                                            selectTime:
                                                d.format(
                                                    "HH:mm:ss"
                                                ),
                                        })
                                    );
                                }}
                            />
                        </div>

                        <div className="space-y-2">
                            <label>
                                {t(
                                    "common.content-094"
                                )}
                            </label>

                            <CustomSelect
                                name='biometricType'
                                value={formData.biometricType}
                                onChange={handleInputChange}
                                options={biometricTypeData}
                            />
                        </div> */}

                        <div className="flex flex-col gap-2">
                            {/* <div className="flex items-center justify-between mb-1 bg-[#F5F8FF] p-4 rounded-md border-l-[6px] border-l-[#0057FF]">
                                <p className="font-semibold">
                                    {t("common.event-images")}
                                </p>
                            </div> */}

                            <CustomTabsV2
                                variant="fullWidth"
                                indicatorColor="none"
                                value={tabValue}
                                onChange={handleChangeTab}
                            >
                                <Tab
                                    label={t("common.browser")}
                                    {...a11yProps(0)}
                                    disableRipple
                                />
                                <Tab
                                    label="Webcam"
                                    {...a11yProps(1)}
                                    disableRipple
                                />
                                <Tab
                                    label={t("common.event-images")}
                                    {...a11yProps(2)}
                                    disableRipple
                                    disabled
                                />
                            </CustomTabsV2>

                            {/* TAB 1: UPLOAD */}
                            <CustomTabPanel value={tabValue} index={0}>
                                <div className="px-1 pt-4">
                                    <div className="grid grid-cols-12 gap-4">
                                        <label className="col-span-12 text-sm font-medium">
                                            {t("common.profilepicture")}
                                        </label>

                                        <div className="col-span-12">
                                            <DropZoneInput
                                                className="w-full"
                                                file={fileImport}
                                                handleInputChange={handleImageInputChange}
                                                handleDropChange={handleFileChange}
                                                name="fileImport"
                                                accept="image/*"
                                            />
                                        </div>

                                        {isDetecting ? (
                                            <div className="col-span-12 flex items-center gap-2 text-sm text-[#6B7280]">
                                                <CircularProgress size={18} />
                                                <span>
                                                    {t("common.detectingface") ||
                                                        "Đang kiểm tra khuôn mặt..."}
                                                </span>
                                            </div>
                                        ) : (
                                            faceMessage && (
                                                <div
                                                    className={`
                                                        col-span-12
                                                        text-sm
                                                        font-medium
                                                        ${faceValid
                                                            ? "text-green-600"
                                                            : "text-red-600"
                                                        }
                                                    `}
                                                >
                                                    {faceMessage}
                                                </div>
                                            )
                                        )}
                                    </div>
                                </div>
                            </CustomTabPanel>

                            {/* TAB 2: WEBCAM */}
                            <CustomTabPanel value={tabValue} index={1}>
                                <div className="w-full flex justify-center py-4">
                                    {cameraError ? (
                                        <div className="w-full max-w-full flex flex-col items-center justify-center gap-4 px-6 text-center">
                                            <VideoIcon48pxSvg className="p-2 bg-[#F3F4F6] rounded-xl" />
                                            <div>
                                                <p className="text-lg font-bold">
                                                    Không tìm thấy camera
                                                </p>
                                                <p className="text-sm text-[#6B7280] mt-1">
                                                    Vui lòng cấp quyền camera
                                                </p>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="w-full flex flex-col items-center gap-4">
                                            {!capturedImage ? (
                                                <>
                                                    <div
                                                        className={`
                                                            relative
                                                            w-full
                                                            max-w-full
                                                            overflow-hidden
                                                            rounded-xl
                                                            border-4
                                                            ${faceValid
                                                                ? "border-green-500"
                                                                : "border-red-500"
                                                            }
                                                        `}
                                                    >
                                                        <Webcam
                                                            ref={webcamRef}
                                                            audio={false}
                                                            screenshotFormat="image/jpeg"
                                                            videoConstraints={videoConstraints}
                                                            onUserMediaError={() =>
                                                                setCameraError(true)
                                                            }
                                                            className="w-full h-auto object-cover aspect-video"
                                                        />

                                                        <div className="absolute bottom-3 left-1/2 -translate-x-1/2">
                                                            <div
                                                                className={`
                                                                    px-3 py-1 rounded-full text-sm font-medium
                                                                    ${faceValid
                                                                        ? "bg-green-500 text-white"
                                                                        : "bg-red-500 text-white"
                                                                    }
                                                                `}
                                                            >
                                                                {faceMessage}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <ControlpanelPrimaryButton
                                                        disabled={!faceValid}
                                                        sx={{
                                                            width: 40,
                                                            minWidth: 40,
                                                            height: 40,
                                                            padding: 0,
                                                        }}
                                                        onClick={capture}
                                                    >
                                                        <CaptureIcon16pxSvg />
                                                    </ControlpanelPrimaryButton>
                                                </>
                                            ) : (
                                                <>
                                                    <div className="relative w-full max-w-full">
                                                        <img
                                                            src={capturedImage}
                                                            alt="Captured"
                                                            className="w-full h-auto border rounded-xl object-cover aspect-video"
                                                        />
                                                        <div className="absolute top-3 right-3 bg-green-500 text-white rounded-full p-1">
                                                            <CheckCircleIcon />
                                                        </div>
                                                    </div>

                                                    <ControlpanelSuccess3thButton
                                                        sx={{
                                                            width: 40,
                                                            minWidth: 40,
                                                            height: 40,
                                                            padding: 0,
                                                        }}
                                                        onClick={handleRetake}
                                                    >
                                                        <RefreshCircleIcon20pxSvgV2 />
                                                    </ControlpanelSuccess3thButton>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </CustomTabPanel>

                            {/* TAB 3: HÌNH ẢNH LỖI (DISABLED) */}
                            <CustomTabPanel value={tabValue} index={2}>
                                <div className="pt-4">
                                    <div className="flex items-center justify-between mb-3">
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setIsImageSectionOpen(
                                                    (prev) => !prev
                                                )
                                            }
                                            className="text-sm font-medium hover:underline ml-auto"
                                        >
                                            {isImageSectionOpen ? (
                                                <ExpandMoreIcon
                                                    sx={{ fontSize: 18 }}
                                                />
                                            ) : (
                                                <ExpandLessIcon
                                                    sx={{ fontSize: 18 }}
                                                />
                                            )}
                                        </button>
                                    </div>

                                    {isImageSectionOpen && (
                                        <>
                                            {dataIMG?.length === 0 ? (
                                                <div className="flex flex-col justify-center items-center h-[200px] gap-2 text-gray-400">
                                                    <ImageIcon
                                                        style={{
                                                            height: 45,
                                                            width: 45,
                                                        }}
                                                    />
                                                    {t("common.no-image")}
                                                </div>
                                            ) : (
                                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
                                                    {dataIMG.map((item) => {
                                                        const isSelected =
                                                            selectedImageId?.uniqueId ===
                                                            item.uniqueId;

                                                        return (
                                                            <div
                                                                key={item.uniqueId}
                                                            >
                                                                <AvatarCard
                                                                    {...item}
                                                                    isSelected={
                                                                        isSelected
                                                                    }
                                                                    onSelect={() =>
                                                                        setSelectedImageId(
                                                                            (prev) =>
                                                                                prev?.uniqueId ===
                                                                                    item.uniqueId
                                                                                    ? null
                                                                                    : item
                                                                        )
                                                                    }
                                                                />
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}

                                            <div className="flex justify-center mt-2">
                                                <Pagination
                                                    page={page + 1}
                                                    count={Math.ceil(
                                                        rowCount /
                                                        RECORD_PER_PAGE
                                                    )}
                                                    onChange={(
                                                        event,
                                                        value
                                                    ) => {
                                                        document.activeElement?.blur();
                                                        setPage(value - 1);
                                                    }}
                                                    color="primary"
                                                />
                                            </div>
                                        </>
                                    )}
                                </div>
                            </CustomTabPanel>
                        </div>
                    </div>
                )}

                {/* STEP 2 */}
                {activeStep === 1 && (
                    <div className="space-y-3 mt-4">
                        {formData.faceImage && (
                            <div className="flex items-start justify-center gap-3">
                                <div className="flex items-center justify-center overflow-hidden border border-gray-200 p-[5px] rounded-[4px]">
                                    <img
                                        src={formData.faceImage}
                                        alt="preview"
                                        className="w-[168px] h-[170px] object-cover rounded-[4px]"
                                    />
                                </div>
                            </div>
                        )}

                        <div>
                            <p>
                                {t(
                                    "common.event-type"
                                )}
                                <span className="text-[#EF4444] ml-1">
                                    *
                                </span>
                            </p>

                            <FormControl
                                component="fieldset"
                                sx={{
                                    ".MuiFormGroup-root.MuiFormGroup-row":
                                    {
                                        gap: "20px",
                                    },
                                }}
                            >
                                <RadioGroup
                                    row
                                    aria-label="Event type"
                                    name="eventType"
                                    value={
                                        formData.eventType
                                    }
                                    onChange={
                                        handleInputChange
                                    }
                                >
                                    <FormControlLabel
                                        value={
                                            1
                                        }
                                        control={
                                            <Radio
                                                size="small"
                                            />
                                        }
                                        label={t(
                                            "attendance.CHECK_IN"
                                        )}
                                    />

                                    <FormControlLabel
                                        value={
                                            2
                                        }
                                        control={
                                            <Radio
                                                size="small"
                                            />
                                        }
                                        label={t(
                                            "attendance.CHECK_OUT"
                                        )}
                                    />
                                </RadioGroup>
                            </FormControl>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label>
                                    {t("common.name")}
                                    <span className="text-red-500">
                                        *
                                    </span>
                                </label>

                                <CustomSelect
                                    name="userId"
                                    value={
                                        formData.userId
                                    }
                                    onChange={
                                        handleInputChange
                                    }
                                    options={data}
                                    className="mt-2 h-10"
                                    dataType="visitors"
                                    placeholder={t(
                                        "common.user-name"
                                    )}
                                />
                            </div>

                            <div className="space-y-2">
                                <label>
                                    {t(
                                        "common.select-time"
                                    )}
                                </label>

                                <CustomDateTimePicker
                                    mode="singleTime"
                                    className="h-10"
                                    value={
                                        formData.selectDate &&
                                            formData.selectTime
                                            ? dayjs(
                                                `${dayjs(
                                                    formData.selectDate
                                                ).format(
                                                    "YYYY-MM-DD"
                                                )} ${formData.selectTime
                                                }`
                                            ).toDate()
                                            : null
                                    }
                                    dateTimeFormat={
                                        dateTimeFormat
                                    }
                                    onChange={(
                                        newDateTime
                                    ) => {
                                        const d =
                                            dayjs(
                                                newDateTime
                                            );

                                        setFormData(
                                            (prev) => ({
                                                ...prev,

                                                selectDate:
                                                    d.toDate(),

                                                selectTime:
                                                    d.format(
                                                        "HH:mm:ss"
                                                    ),
                                            })
                                        );
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </DialogContent>

            <DialogActions>
                <div className="flex gap-2 px-4 py-4">
                    {activeStep === 0 ? (
                        <>
                            <ControlpanelCancelButton
                                onClick={handleCloseDialog}
                            >
                                <p>{t("button.cancel")}</p>
                            </ControlpanelCancelButton>

                            <ControlpanelPrimaryButton
                                onClick={handleNext}
                            >
                                <p>{t("button.next")}</p>
                            </ControlpanelPrimaryButton>
                        </>
                    ) : (
                        <>
                            <ControlpanelCancelButton
                                onClick={handleBack}
                            >
                                <p>{t("button.back")}</p>
                            </ControlpanelCancelButton>

                            <ControlpanelPrimaryButton
                                onClick={handleSubmit}
                            >
                                <p>{t("button.confirm")}</p>
                            </ControlpanelPrimaryButton>
                        </>
                    )}
                </div>
            </DialogActions>
        </CustomDialog>
    );
};

export default AddEventLogDialog;